import React from 'react';
import { styled } from '@compiled/react';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { GetActiveFieldHeaderInfo } from '../../../common/types.tsx';
import { FieldConfigurationDefaultValue } from '../../../common/ui/field-active/styled.tsx';
import { messages } from './messages.tsx';
import { getEcosystemAppsRestrictions } from './utils.tsx';

type Props = Readonly<{
	intl: IntlShape;
	forgeDisplayConditions: ForgeDisplayConditionsWarningProps | null;
	accessNarrowedApp: string | null;
}>;

const HiddenInAccessNarrowingWarnings = ({
	intl,
	appName,
}: {
	intl: IntlShape;
	appName: string;
}) => (
	<SectionMessage appearance="warning">
		{intl.formatMessage(messages.hiddenInAccessNarrowing, {
			appName,
		})}
	</SectionMessage>
);

type ForgeDisplayConditionsWarningProps = Readonly<{
	shouldDisplayWarning: boolean;
	issueTypeDisplayConditionRestrictions: {
		isHiddenInDisplayConditions: boolean;
		filteredIssueTypes: string[];
		areAllIssueTypesFiltered: boolean;
	} | null;
	projectDisplayConditionRestrictions: {
		isHiddenInDisplayConditions: boolean;
	} | null;
	appName: string;
}>;

const HiddenInForgeDisplayCondiditionWarnings = ({
	intl,
	forgeDisplayConditions: {
		appName,
		issueTypeDisplayConditionRestrictions,
		projectDisplayConditionRestrictions,
	},
}: {
	intl: IntlShape;
	forgeDisplayConditions: ForgeDisplayConditionsWarningProps;
}) => {
	if (
		issueTypeDisplayConditionRestrictions?.isHiddenInDisplayConditions &&
		issueTypeDisplayConditionRestrictions.filteredIssueTypes.length !== 0
	) {
		return (
			<SectionMessage appearance="warning">
				{issueTypeDisplayConditionRestrictions.areAllIssueTypesFiltered
					? intl.formatMessage(messages.hiddenInForgeDisplayConditionsStrict, {
							appName,
						})
					: intl.formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.hiddenInForgeDisplayConditionsWithIssueTypesIssueTermRefresh
								: messages.hiddenInForgeDisplayConditionsWithIssueTypes,
							{
								appName,
								issueTypesCount: issueTypeDisplayConditionRestrictions.filteredIssueTypes.length,
								issueTypes: issueTypeDisplayConditionRestrictions.filteredIssueTypes
									.map((issueType) => `"${issueType}"`)
									.join(', '),
							},
						)}
			</SectionMessage>
		);
	}

	if (projectDisplayConditionRestrictions?.isHiddenInDisplayConditions) {
		return (
			<SectionMessage appearance="warning">
				{intl.formatMessage(messages.hiddenInForgeDisplayConditionsStrict, {
					appName,
				})}
			</SectionMessage>
		);
	}

	return (
		<SectionMessage appearance="warning">
			{intl.formatMessage(messages.hiddenInForgeDisplayConditions, {
				appName,
			})}
		</SectionMessage>
	);
};

export const RestrictedEcosystemAppFieldWarnings = ({
	intl,
	forgeDisplayConditions,
	accessNarrowedApp,
}: Props) => (
	<RestrictedFieldDetails>
		{accessNarrowedApp && (
			<HiddenInAccessNarrowingWarnings intl={intl} appName={accessNarrowedApp} />
		)}
		{forgeDisplayConditions?.shouldDisplayWarning && (
			<HiddenInForgeDisplayCondiditionWarnings
				intl={intl}
				forgeDisplayConditions={forgeDisplayConditions}
			/>
		)}
	</RestrictedFieldDetails>
);

export const ecosystemAppFieldWarning: GetActiveFieldHeaderInfo = ({
	associatedField,
	layoutOwners,
	intl,
	projectContext,
}) => {
	const { forgeDisplayConditionsRestrictions, accessNarrowingRestrictedApp } =
		getEcosystemAppsRestrictions(
			associatedField.availability,
			layoutOwners,
			projectContext,
			associatedField.provider,
		);

	if (forgeDisplayConditionsRestrictions?.shouldDisplayWarning || accessNarrowingRestrictedApp) {
		return {
			icon: {
				type: 'warning',
				label: messages.restrictedEcosystemAppFieldIconLabel,
			},
			longText: (
				<RestrictedEcosystemAppFieldWarnings
					intl={intl}
					forgeDisplayConditions={forgeDisplayConditionsRestrictions}
					accessNarrowedApp={accessNarrowingRestrictedApp}
				/>
			),
		};
	}

	return undefined;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RestrictedFieldDetails = styled(FieldConfigurationDefaultValue)({
	flexDirection: 'column',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> *:not(:last-child)': {
		marginBottom: token('space.200'),
	},
});
